import { gql, useLazyQuery } from '@apollo/client';
import { useGetQuantifyTableEQLProps } from '@modules/quantify/hooks/useFetchQuantifyTableData/useGetQuantifyTableEQLProps';
import {
  BucketParamsFragmentDoc,
  ExecuteEqlQueryVariables,
  Query,
} from 'generated/graphql';
import { useCallback, useRef } from 'react';
import { AndOfErrors } from 'v2/core/errors/errors';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { AndStatuses } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

export const GetAggregatesResponseViewFragmentDoc = gql`
  fragment GetAggregatesResponseView on GetAggregatesResponse {
    dataV2 {
      bucket {
        ...BucketParams
      }
      values {
        value
        queryID
        timestamp
        entitySentiment
        intermediates {
          func
          value {
            anomaly {
              zScore
              mean
              isAnomalous
            }
            applyTaxonomy {
              isRelevant
            }
            nps {
              detractorsPercentage
              promotersPercentage
            }
            csat {
              satisfiedResponses
              totalResponses
            }
            mean {
              totalValue
              weightedSum
            }
          }
        }
      }
    }
    hits
    valueRange {
      start
      end
    }
  }
  ${BucketParamsFragmentDoc}
`;

export const GetDeltaAggregatesResponseViewFragmentDoc = gql`
  fragment GetDeltaAggregatesResponseView on GetDeltaAggregatesResponse {
    dataV2 {
      bucket {
        ...BucketParams
      }
      prevValue
      currValue
      absoluteDelta
      relativeDelta
      prevIntermediates {
        func
        value {
          applyTaxonomy {
            isRelevant
          }
          nps {
            detractorsPercentage
            promotersPercentage
          }
          csat {
            satisfiedResponses
            totalResponses
          }
          mean {
            totalValue
            weightedSum
          }
        }
      }
      currIntermediates {
        func
        value {
          applyTaxonomy {
            isRelevant
          }
          nps {
            detractorsPercentage
            promotersPercentage
          }
          csat {
            satisfiedResponses
            totalResponses
          }
          mean {
            totalValue
            weightedSum
          }
        }
      }
    }
    aggregateHits: hits
    valueRange {
      start
      end
    }
  }
  ${BucketParamsFragmentDoc}
`;

export const ExecuteEqlDocument = gql`
  query executeEQL($tenantID: ID!, $expression: String!, $cursor: String) {
    executeEQL(
      request: { tenantID: $tenantID, expression: $expression, cursor: $cursor }
    ) {
      result {
        ...GetAggregatesResponseView
        ...GetDeltaAggregatesResponseView
      }
    }
  }
  ${GetAggregatesResponseViewFragmentDoc}
  ${GetDeltaAggregatesResponseViewFragmentDoc}
`;

interface useExecuteQuantifyTableEqlParams {
  shouldFetchCount?: boolean;
}

export function useExecuteQuantifyTableEql({
  shouldFetchCount,
}: useExecuteQuantifyTableEqlParams) {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const eqlProps = useGetQuantifyTableEQLProps({ shouldFetchCount });
  const {
    eql,
    status: sStatus,
    error: sError,
  } = useGetEQLExpression({ tenantID, eqlProps });

  const [lazyFunction, { loading, error: eError, called }] = useLazyQuery<
    Query,
    ExecuteEqlQueryVariables
  >(ExecuteEqlDocument, {
    // To skip network calls on component update
    // Bug: https://github.com/apollographql/apollo-client/issues/7484
    fetchPolicy: 'network-only',
  });

  const dependencies = { eql, tenantID, lazyFunction };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const eStatus = apolloStatusToQueryStatus(loading, called, undefined, eError);
  const status = AndStatuses([sStatus, eStatus]);
  const error = AndOfErrors([sError, eError]);

  const lazyFunctionWrapper = useCallback(() => {
    if (!dependenciesRef.current.eql) {
      return;
    }

    return dependenciesRef.current.lazyFunction({
      variables: {
        tenantID: dependenciesRef.current.tenantID,
        expression: dependenciesRef.current.eql,
      },
    });
  }, []);

  return [
    lazyFunctionWrapper,
    {
      error,
      status,
    },
  ] as const;
}
